/**
 * DAC customization: show custom.variant_image on the PDP gallery when variant cards
 * are enabled. Restore painted slides on each variant change. Slide to existing product
 * media when the file is already in the gallery; otherwise paint a later slide and slide to it.
 */
import { ThemeEvents } from "@theme/events";

const PICKER_SELECTOR = "variant-picker[data-variant-card-media]";
const ORIGINAL_SRC = "dacOriginalSrc";
const ORIGINAL_SRCSET = "dacOriginalSrcset";
const ORIGINAL_DATA_SRCSET = "dacOriginalDataSrcset";
const ORIGINAL_MEDIA_SRC = "dacOriginalMediaSrc";
const SLIDE_MS = 300;
const pendingRestoreTimers = new WeakMap();

/**
 * @typedef {Object} VariantCardImage
 * @property {string} src
 * @property {string} srcset
 */

/**
 * @param {Element | null | undefined} picker
 * @returns {boolean}
 */
const isProductPagePicker = (picker) =>
  Boolean(picker instanceof HTMLElement && !picker.closest("product-card, quick-add-dialog, dialog"));

/**
 * @param {Element} picker
 * @returns {Record<string, VariantCardImage>}
 */
const readImageMap = (picker) => {
  const script = picker.querySelector("script[data-variant-card-images]");
  if (!script?.textContent) return {};

  try {
    return JSON.parse(script.textContent);
  } catch (error) {
    console.warn("DAC variant card media: failed to parse image map", error);
    return {};
  }
};

/**
 * @param {Element} picker
 * @param {string | number} variantId
 * @returns {number}
 */
const getCardIndex = (picker, variantId) => {
  const safeId = typeof CSS !== "undefined" && CSS.escape ? CSS.escape(String(variantId)) : String(variantId);
  const input = picker.querySelector(`input[data-variant-id="${safeId}"][data-variant-card-index]`);
  if (!(input instanceof HTMLElement)) return -1;

  const index = parseInt(input.dataset.variantCardIndex ?? "", 10);
  return Number.isNaN(index) ? -1 : index;
};

/**
 * @param {Element} picker
 * @returns {HTMLElement | null}
 */
const getGallery = (picker) => {
  const scope = picker.closest(".shopify-section");
  const gallery = scope?.querySelector("media-gallery");
  return gallery instanceof HTMLElement ? gallery : null;
};

/**
 * @param {HTMLElement} gallery
 * @returns {import("@theme/carousel").CarouselComponent | null}
 */
const getCarousel = (gallery) => {
  const fromRefs = gallery.refs?.carousel;
  if (fromRefs) return fromRefs;

  const node = gallery.querySelector("carousel-slider");
  return node instanceof HTMLElement ? /** @type {import("@theme/carousel").CarouselComponent} */ (node) : null;
};

/**
 * @param {HTMLImageElement} img
 */
const storeOriginal = (img) => {
  if (img.dataset[ORIGINAL_SRC] != null) return;

  img.dataset[ORIGINAL_SRC] = img.getAttribute("src") || "";
  img.dataset[ORIGINAL_SRCSET] = img.getAttribute("srcset") || "";
  img.dataset[ORIGINAL_DATA_SRCSET] = img.getAttribute("data-srcset") || "";

  const media = img.closest("[data-media-src]");
  if (media instanceof HTMLElement && media.dataset[ORIGINAL_MEDIA_SRC] == null) {
    media.dataset[ORIGINAL_MEDIA_SRC] = media.dataset.mediaSrc || "";
  }
};

/**
 * @param {HTMLImageElement} img
 * @param {VariantCardImage | null} image
 */
const applyImage = (img, image) => {
  storeOriginal(img);

  const media = img.closest("[data-media-src]");
  const src = image?.src || img.dataset[ORIGINAL_SRC] || "";
  const srcset = image?.srcset || img.dataset[ORIGINAL_SRCSET] || "";
  const dataSrcset = image?.srcset || img.dataset[ORIGINAL_DATA_SRCSET] || "";

  if (src) img.src = src;
  img.srcset = srcset;
  if (img.hasAttribute("data-srcset") || dataSrcset) {
    img.setAttribute("data-srcset", dataSrcset);
  }

  if (media instanceof HTMLElement) {
    const mediaSrc = image?.src || media.dataset[ORIGINAL_MEDIA_SRC] || "";
    if (mediaSrc) media.dataset.mediaSrc = mediaSrc;
  }
};

/**
 * Restore all painted slides except the one at `keepIndex`.
 * Off-screen restores are invisible; restoring the kept slide is handled separately.
 * @param {HTMLElement} gallery
 * @param {HTMLElement[]} slides
 * @param {number} keepIndex
 */
const restoreAllExcept = (gallery, slides, keepIndex) => {
  slides.forEach((slide, i) => {
    if (i === keepIndex) return;
    const img = slide.querySelector("img");
    if (img instanceof HTMLImageElement && img.dataset[ORIGINAL_SRC] != null) {
      applyImage(img, null);
      const mediaId = slide.dataset.mediaId;
      if (mediaId) {
        for (const thumbImg of getMatchingThumbImages(gallery, mediaId)) {
          applyImage(thumbImg, null);
        }
      }
    }
  });
};

/**
 * Restore non-target painted slides after the carousel motion finishes.
 * @param {HTMLElement} gallery
 * @param {HTMLElement[]} slides
 * @param {number} keepIndex
 * @param {boolean} animate
 */
const scheduleRestoreAllExcept = (gallery, slides, keepIndex, animate) => {
  const pending = pendingRestoreTimers.get(gallery);
  if (pending) window.clearTimeout(pending);

  const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches === true;
  const delay = animate && !reducedMotion ? SLIDE_MS : 0;

  const timer = window.setTimeout(() => {
    restoreAllExcept(gallery, slides, keepIndex);
    pendingRestoreTimers.delete(gallery);
  }, delay);

  pendingRestoreTimers.set(gallery, timer);
};

/**
 * Filename key for Shopify CDN URLs, ignoring size/query params.
 * @param {string} url
 * @returns {string}
 */
const imageFileKey = (url) => {
  if (!url) return "";

  try {
    const path = new URL(url, window.location.origin).pathname;
    let file = decodeURIComponent(path.split("/").pop() || "").toLowerCase();
    file = file.replace(
      /_(?:pico|icon|thumb|small|compact|medium|large|grande|original|master|\d+x(?:\d+)?)(?=\.[a-z0-9]+$)/i,
      ""
    );
    return file;
  } catch {
    return "";
  }
};

/**
 * @param {HTMLElement} slide
 * @returns {string}
 */
const slideCurrentKey = (slide) => {
  const img = slide.querySelector("img");
  if (img instanceof HTMLImageElement) {
    return imageFileKey(img.src);
  }
  return imageFileKey(slide.dataset.mediaSrc || "");
};

/**
 * @param {HTMLElement} slide
 * @returns {string}
 */
const slideOriginalKey = (slide) => {
  const img = slide.querySelector("img");
  if (img instanceof HTMLImageElement && img.dataset[ORIGINAL_SRC]) {
    return imageFileKey(img.dataset[ORIGINAL_SRC]);
  }
  if (slide.dataset[ORIGINAL_MEDIA_SRC]) {
    return imageFileKey(slide.dataset[ORIGINAL_MEDIA_SRC]);
  }
  return imageFileKey(slide.dataset.mediaSrc || "");
};

/**
 * @param {HTMLElement} slide
 * @param {string} src
 * @returns {boolean}
 */
const slideShowsImage = (slide, src) => slideCurrentKey(slide) === imageFileKey(src);

/**
 * @param {HTMLElement} gallery
 * @returns {HTMLElement[]}
 */
const getGallerySlides = (gallery) => {
  const wrapper = gallery.querySelector(".media-gallery__carousel-wrapper");
  if (!(wrapper instanceof HTMLElement)) return [];

  return Array.from(wrapper.children).filter(
    (child) => child instanceof HTMLElement && child.dataset.mediaId
  );
};

/**
 * @param {HTMLElement[]} slides
 * @param {string} src
 * @returns {number}
 */
const findMatchingSlideIndex = (slides, src) => slides.findIndex((slide) => slideShowsImage(slide, src));

/**
 * @param {Record<string, VariantCardImage>} imageMap
 * @returns {Set<string>}
 */
const reservedImageKeys = (imageMap) => {
  const keys = new Set();
  for (const image of Object.values(imageMap)) {
    const key = imageFileKey(image?.src || "");
    if (key) keys.add(key);
  }
  return keys;
};

/**
 * @param {HTMLElement} gallery
 * @returns {number}
 */
const getActiveIndex = (gallery) => {
  const swiper = getCarousel(gallery)?.swiperInstance;
  if (swiper && !swiper.destroyed && typeof swiper.activeIndex === "number") {
    return swiper.activeIndex;
  }
  return 0;
};

/**
 * @param {HTMLElement} gallery
 * @param {string} mediaId
 * @returns {HTMLImageElement[]}
 */
const getMatchingThumbImages = (gallery, mediaId) => {
  const safeId = typeof CSS !== "undefined" && CSS.escape ? CSS.escape(mediaId) : mediaId;
  const thumbs = gallery.querySelectorAll(
    `.media-gallery__carousel-thumbnails [data-media-id="${safeId}"] img, .media-gallery__grid-thumbnails [data-media-id="${safeId}"] img`
  );

  return Array.from(thumbs).filter((node) => node instanceof HTMLImageElement);
};

/**
 * @param {HTMLElement} gallery
 * @param {string} mediaId
 */
const updateThumbActive = (gallery, mediaId) => {
  const id = String(mediaId);

  const carouselThumbs = gallery.querySelector(".media-gallery__carousel-thumbnails");
  if (carouselThumbs) {
    for (const thumb of carouselThumbs.querySelectorAll("[data-media-id]")) {
      if (!(thumb instanceof HTMLElement)) continue;
      thumb.classList.toggle("is-active", thumb.dataset.mediaId === id);
    }
  }

  const gridThumbs = gallery.querySelector(".media-gallery__grid-thumbnails");
  if (gridThumbs) {
    for (const thumb of gridThumbs.querySelectorAll("[data-media-id]")) {
      if (!(thumb instanceof HTMLElement)) continue;
      const isMatch = thumb.dataset.mediaId === id;
      thumb.classList.toggle("is-active", isMatch);
      if (isMatch) thumb.setAttribute("aria-current", "true");
      else thumb.removeAttribute("aria-current");
    }
  }
};

/**
 * @param {HTMLElement} gallery
 * @param {number} index
 * @param {boolean} animate
 */
const slideToIndex = (gallery, index, animate) => {
  const carousel = getCarousel(gallery);
  const swiper = carousel?.swiperInstance;
  if (!swiper || swiper.destroyed || index < 0) return false;

  const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches === true;
  const speed = animate && !reducedMotion ? SLIDE_MS : 0;

  if (typeof carousel.goToSlide === "function") {
    carousel.goToSlide(index, speed, true);
  } else {
    swiper.slideTo(index, speed, true);
  }

  return true;
};

/**
 * Later cards paint the next slide (not the visible first image) so the gallery can animate.
 * Skip slides whose original file is already a variant metafield (e.g. the tub).
 * Extra cards use the last usable slide when the gallery is shorter.
 * @param {HTMLElement[]} slides
 * @param {number} cardIndex
 * @param {Set<string>} reservedKeys
 * @returns {number}
 */
const getPaintSlideIndex = (slides, cardIndex, reservedKeys) => {
  const last = slides.length - 1;
  if (last < 0) return -1;
  if (cardIndex <= 0) return 0;

  const start = Math.min(Math.max(cardIndex, 1), last);
  for (let index = start; index <= last; index += 1) {
    const originalKey = slideOriginalKey(slides[index]);
    if (!originalKey || !reservedKeys.has(originalKey)) return index;
  }

  return last;
};

/**
 * @param {HTMLElement} gallery
 * @param {HTMLElement} slide
 * @param {VariantCardImage} image
 * @returns {boolean}
 */
const paintSlide = (gallery, slide, image) => {
  if (slide.dataset.mediaType !== "image") return false;

  const slideImage = slide.querySelector("img");
  if (!(slideImage instanceof HTMLImageElement)) return false;

  applyImage(slideImage, image);

  const mediaId = slide.dataset.mediaId;
  if (mediaId) {
    for (const thumbImage of getMatchingThumbImages(gallery, mediaId)) {
      applyImage(thumbImage, image);
    }
  }

  return true;
};

/**
 * @param {HTMLElement} gallery
 * @param {number} index
 * @param {boolean} animate
 */
const showSlide = (gallery, index, animate) => {
  const slides = getGallerySlides(gallery);
  const slide = slides[index];
  slideToIndex(gallery, index, animate);
  if (slide?.dataset.mediaId) updateThumbActive(gallery, slide.dataset.mediaId);
};

/**
 * @param {Element} picker
 * @param {string | number | undefined} variantId
 * @param {boolean} [animate]
 */
const applyVariantCardMedia = (picker, variantId, animate = false) => {
  if (!isProductPagePicker(picker) || variantId == null || variantId === "") return;

  const gallery = getGallery(picker);
  if (!gallery) return;

  const imageMap = readImageMap(picker);
  const image = imageMap[String(variantId)];
  const cardIndex = getCardIndex(picker, variantId);
  if (!image?.src || cardIndex < 0) return;

  const slides = getGallerySlides(gallery);
  if (!slides.length) return;

  // If the metafield image is already in the gallery, slide to it first, then restore old painted slots.
  const existingIndex = findMatchingSlideIndex(slides, image.src);
  if (existingIndex !== -1) {
    showSlide(gallery, existingIndex, animate);
    scheduleRestoreAllExcept(gallery, slides, existingIndex, animate);
    return;
  }

  const targetIndex = getPaintSlideIndex(slides, cardIndex, reservedImageKeys(imageMap));
  const targetSlide = slides[targetIndex];
  if (!targetSlide) return;

  // Paint first so the destination slide is ready during the transition, then restore old slots after motion ends.
  if (!slideShowsImage(targetSlide, image.src)) {
    paintSlide(gallery, targetSlide, image);
  }
  showSlide(gallery, targetIndex, animate);
  scheduleRestoreAllExcept(gallery, slides, targetIndex, animate);
};

/**
 * @param {Element} picker
 */
const applyFromPickerState = (picker) => {
  const selectedScript = picker.querySelector(
    'script[type="application/json"]:not([data-variants-cache]):not([data-variant-card-images])'
  );
  let variantId = "";

  if (selectedScript?.textContent) {
    try {
      variantId = JSON.parse(selectedScript.textContent)?.id ?? "";
    } catch {
      variantId = "";
    }
  }

  if (!variantId) {
    const checked = picker.querySelector("input[type='radio']:checked[data-variant-id]");
    variantId = checked instanceof HTMLElement ? checked.dataset.variantId || "" : "";
  }

  applyVariantCardMedia(picker, variantId, false);
};

/**
 * Block the native instant featured-media jump; we handle gallery navigation.
 * @param {Event} event
 */
const onOptimisticSwitch = (event) => {
  const gallery = event.target;
  if (!(gallery instanceof HTMLElement) || gallery.tagName.toLowerCase() !== "media-gallery") return;

  const picker = gallery.closest(".shopify-section")?.querySelector(PICKER_SELECTOR);
  if (!isProductPagePicker(picker) || !picker) return;

  const variantId = "detail" in event ? event.detail?.variant?.id : undefined;
  if (variantId == null) return;

  const image = readImageMap(picker)[String(variantId)];
  if (!image?.src || getCardIndex(picker, variantId) < 0) return;

  event.stopImmediatePropagation();
};

/**
 * @param {Event} event
 */
const onVariantUpdate = (event) => {
  const target = event.target;
  const picker = target instanceof Element ? target.closest(PICKER_SELECTOR) : null;
  if (!picker) return;

  const variantId = "detail" in event ? event.detail?.resource?.id : undefined;
  requestAnimationFrame(() => applyVariantCardMedia(picker, variantId, true));
};

document.addEventListener("media:switch-optimistic", onOptimisticSwitch, true);
document.addEventListener(ThemeEvents.variantUpdate, onVariantUpdate);

document.querySelectorAll(PICKER_SELECTOR).forEach((picker) => applyFromPickerState(picker));
